import React, { useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import clsx from "clsx";
import { rotationCriteriaOptionalFields } from "../../../../Enums/RotationCriteriaOptionalFields.Enum";
import {
  CustomCard,
  CustomSwitch,
  ReskinningInputs,
} from "../../../../Components";
import {
  OptionalField,
  ActiveOptionalField,
  MandatoryFields,
  OptionalFieldAccordion,
} from "../";
import {
  useIsDesktop,
  useSelectedTheme,
  useTranslate,
} from "../../../../Hooks";

import useStyles from "./styles";

import { SwitchHorizontal2Icon } from "../../../../assets/icons";

function AddNewRotationForm({
  generateStepperActions,
  formikRef,
  formValues,
  activeOptionalFields,
}) {
  const { isDesktop } = useIsDesktop();
  const styles = useStyles();
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const [optionalFields, setOptionalFields] = useState(
    rotationCriteriaOptionalFields
  );
  const {
    theme: { palette },
  } = useSelectedTheme();

  const formInitialValues = {
    isActive: false,
    label: "",
    order: 0,
    buyer: false,
    tenant: false,
    vip: false,
    vvip: false,
    normal: false,
    isBulk: 0,
    rotationSchemaReferredBys: [],
    mediaDetails: [],
    developer: [],
    unitType: [],
    priceRange: [0, 600000],
    size: [0, 10000],
    bedrooms: [0, 8],
    bathrooms: [0, 8],
    propertyCampaignIds: [],
    rotationPreferredLanguages: [],
    rotationSchemaContactCLasses: [],
    rotationSchemaDeveloperIds: [],
    rotationSchemaLeadsType: [],
    rotationSchemeCities: [],
    rotationSchemeCommunities: [],
    rotationSchemeCountries: [],
    rotationSchemeDistricts: [],
    rotationSchemeMedias: [],
    rotationSchemeProperties: [],
    rotationSchemeRanges: [],
    rotationSchemeSubCommunities: [],
    rotationSchemeUnitTypes: [],
    rotationSchemaMethodOfContacts: [],
    ...formValues,
  };

  useEffect(() => {
    //check if there were optional fields selected
    if (activeOptionalFields) {
      setOptionalFields({
        ...optionalFields,
        ...activeOptionalFields,
      });
    }
  }, []);

  const onOptionalFieldCloseHandler = (fieldKey) => {
    setOptionalFields({
      ...optionalFields,
      [fieldKey]: { ...optionalFields[fieldKey], isActive: false },
    });
  };

  const getValidationSchema = () => {
    return Yup.object({
      label: Yup.string().required(translate("error-rotation-name-required")),
      isActive: Yup.boolean(),
      order: Yup.number()
        .typeError(translate("error-order-is-number"))
        .required(translate("error-order-required"))
        .min(1, translate("error-order-bigger-than-1")),
      buyer: Yup.boolean(),
      tenant: Yup.boolean(),
      vip: Yup.boolean(),
      vvip: Yup.boolean(),
      normal: Yup.boolean(),
      isBulk: Yup.number(),
      rotationSchemaReferredBys: Yup.array()
        .of(
          Yup.object().shape({
            userId: Yup.string().required("User ID is required"),
            fullName: Yup.string().required("Full name is required"),
          })
        )
        .min(1, translate("error-refer-by-required")),
    })
      .test(
        "at-least-one-lead-category",
        "At least one lead category must be selected",
        function (value) {
          const { vip, vvip, normal } = value || {};
          if (vip || vvip || normal) return true;
          return this.createError({
            path: "vip", // attaches the error to vip to show error
            message: translate("error-lead-class-not-selected"),
          });
        }
      )
      .test(
        "at-least-one-lead-type",
        "At least one lead type must be selected",
        function (value) {
          const { buyer, tenant } = value || {};
          if (buyer || tenant) return true;
          return this.createError({
            path: "buyer",
            message: translate("error-lead-type-not-selected"),
          });
        }
      );
  };

  const onSelectOptionalFieldHandler = (selected, fieldData) => {
    setOptionalFields({
      ...optionalFields,
      [fieldData]: { ...optionalFields[fieldData], isActive: selected },
    });
  };

  return (
    <>
      <Box marginTop="32px">
        <Box className={styles.stepperActionContainer}>
          <div>
            <Typography
              className={styles.secondaryTitle}
              variant="h6"
              component="h2"
            >
              {translate("rotation-criteria")}
            </Typography>
            <p className={styles.subText}>
              {translate("schema-creation-subtitle")}
            </p>
          </div>
          <Box
            display="flex"
            flexDirection="row-reverse"
            style={{ gap: "12px", maxHeight: "40px" }}
          >
            {generateStepperActions(optionalFields)}
          </Box>
        </Box>
        <Formik
          innerRef={formikRef}
          initialValues={formInitialValues}
          validationSchema={getValidationSchema()}
        >
          {({ values }) => (
            <Form>
              <Box display="flex" className={styles.contentWrapper}>
                <CustomCard
                  boxShadow="xs"
                  borderRadius="xl"
                  borderColor="secondary"
                  classes={styles.cardContainer}
                >
                  <Typography
                    className={styles.subtitle}
                    component="h3"
                    variant="h6"
                  >
                    {translate("criteria")}
                  </Typography>
                  <p className={clsx(styles.subText, styles.criteriaSubText)}>
                    {translate("optional-criteria-subtitle")}
                  </p>
                  <ReskinningInputs
                    inputPlaceholder={translate("search-input-placeholder")}
                    overInputIcon
                    fieldClasses={styles.inputwrapper}
                    onInputChanged={(e) => {
                      const filteredObj = Object.fromEntries(
                        Object.entries(rotationCriteriaOptionalFields).filter(
                          ([_, item]) =>
                            item.value
                              .toLowerCase()
                              .includes(e.target.value.toLowerCase())
                        )
                      );
                      setOptionalFields({ ...filteredObj });
                    }}
                    rootCustomClasses={styles.inputwrapper}
                  />
                  {!isDesktop && (
                    <OptionalFieldAccordion
                      onClickHandler={onSelectOptionalFieldHandler}
                      optionalFields={optionalFields}
                    />
                  )}
                  {isDesktop &&
                    Object.keys(optionalFields).map((option) => (
                      <OptionalField
                        fieldData={optionalFields[option]}
                        onClickHandler={onSelectOptionalFieldHandler}
                        key={option}
                      />
                    ))}
                </CustomCard>
                {isDesktop && (
                  <SwitchHorizontal2Icon
                    fill={palette.utility.utility_gray_400}
                    style={{ alignSelf: "center" }}
                  />
                )}
                <CustomCard
                  boxShadow="xs"
                  borderRadius="xl"
                  borderColor="secondary"
                  classes={`${styles.cardContainer} ${styles.formContent}`}
                >
                  <Box
                    className={styles.switchWrapper}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <div>
                      <Typography
                        className={styles.subtitle}
                        component="h3"
                        variant="h6"
                      >
                        {translate("selected-rotation-criteria")}
                      </Typography>
                      <p className={styles.subText}>
                        {translate("rotation-form-subtitle")}
                      </p>
                    </div>
                    <CustomSwitch
                      label={translate("active")}
                      size={"md"}
                      checked={values.isActive}
                      onChange={(e) =>
                        formikRef.current.setFieldValue(
                          "isActive",
                          e.target.checked
                        )
                      }
                    />
                  </Box>
                  <MandatoryFields />
                  <ActiveOptionalField
                    activeOptionalFields={Object.values(optionalFields).filter(
                      (option) => option.isActive
                    )}
                    formValues={formValues}
                    onCloseHandler={onOptionalFieldCloseHandler}
                  />
                </CustomCard>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
}

export default AddNewRotationForm;
